import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import {
  ArrowRight, ArrowLeft, Heart, Sparkles, BookOpen, MessageCircle,
  Milestone, Feather, RotateCcw,
} from 'lucide-react';

/** Attachment Styles Learning Hub — the entry point in the Learning portal.
 *  Flow: intro (or result summary) → take/retake assessment → personalized guidance
 *  → recommended lessons → coach conversation → growth plan → journal reflection. */
export default function AttachmentHub() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get('/attachment/hub')
      .then(r => setData(r.data))
      .catch(() => setData({ taken: false, intro: null }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-[#F9F8F6] p-10 text-navy/60" data-testid="hub-loading">Loading…</div>;
  }

  // Not taken yet — show educational intro + CTA to start.
  if (!data?.taken) {
    const intro = data?.intro || {
      eyebrow: 'Attachment Styles',
      title: 'Learn how you connect.',
      body: 'A warm reflection tool. Not a diagnosis.',
      what_you_get: [],
      cta_label: 'Start the assessment',
      cta_to: '/app/attachment/take',
    };
    return <IntroView intro={intro}/>;
  }

  // Taken — show hub with result + recommendations.
  return <ResultHub data={data} onRetake={() => nav('/app/attachment/take')}/>;
}

function IntroView({ intro }) {
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-3xl mx-auto p-6 lg:p-10">
        <Link to="/app/learn" data-testid="hub-back-learn"
          className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-6">
          <ArrowLeft size={14}/> Back to Brotherhood University
        </Link>

        <div className="bg-navy text-white rounded-3xl p-8 lg:p-12 card-soft" data-testid="hub-intro">
          <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold inline-flex items-center gap-2">
            <Heart size={12}/> {intro.eyebrow}
          </p>
          <h1 className="font-heading text-3xl lg:text-5xl font-light leading-tight mt-4">
            {intro.title}
          </h1>
          <p className="text-white/75 text-base lg:text-lg mt-4 leading-relaxed">
            {intro.body}
          </p>

          {intro.what_you_get?.length > 0 && (
            <ul className="mt-8 space-y-3" data-testid="hub-intro-list">
              {intro.what_you_get.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm lg:text-base text-white/85">
                  <span className="w-6 h-6 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xs mt-0.5">{i+1}</span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          )}

          <Link to={intro.cta_to} data-testid="hub-start-cta"
            className="mt-8 inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-medium hover:bg-gold-soft transition-smooth">
            {intro.cta_label} <ArrowRight size={16}/>
          </Link>
        </div>

        <p className="text-xs text-navy/50 mt-6 text-center leading-relaxed">
          Educational, not diagnostic. Adapted from Experiences in Close Relationships—Revised (ECR-R)
          research and framed for Black and Brown men, and men of color in our communities.
        </p>
      </div>
    </div>
  );
}

function ResultHub({ data, onRetake }) {
  const { profile, result, ai_advice, recommended_lessons, coach, growth_plan, journal } = data;
  const max = result?.anxiety_max || 16;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-3xl mx-auto p-6 lg:p-10 space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/app/learn" data-testid="hub-back-learn"
            className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
            <ArrowLeft size={14}/> Back to Brotherhood University
          </Link>
          <button onClick={onRetake} data-testid="hub-retake"
            className="inline-flex items-center gap-1.5 text-sm text-navy/60 hover:text-navy">
            <RotateCcw size={13}/> Retake
          </button>
        </div>

        {/* Result summary card */}
        <div className="bg-navy text-white rounded-3xl p-7 lg:p-10 card-soft" data-testid="hub-result">
          <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">Your Attachment Style</p>
          <h1 className="font-heading text-3xl lg:text-5xl font-light leading-tight mt-3" data-testid="hub-style-title">
            {profile.title}
          </h1>
          <p className="text-white/75 text-sm lg:text-base mt-4 leading-relaxed">{profile.summary}</p>

          <div className="grid grid-cols-2 gap-3 lg:gap-4 mt-6">
            <ScoreCard label="Anxiety axis" value={result.anxiety_score} max={max} testid="hub-score-anxiety"/>
            <ScoreCard label="Avoidance axis" value={result.avoidance_score} max={max} testid="hub-score-avoidance"/>
          </div>
        </div>

        {/* AI advice */}
        {ai_advice && (
          <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="hub-ai-advice">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-gold"/>
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Personalized guidance</p>
            </div>
            <MarkdownLite text={ai_advice}/>
            <p className="text-[11px] text-navy/40 mt-4">
              Generated by AI to reflect your specific pattern. Not therapy, not a diagnosis.
            </p>
          </div>
        )}

        {/* Recommended lessons */}
        {recommended_lessons?.length > 0 && (
          <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="hub-lessons">
            <SectionHeader icon={BookOpen} eyebrow="Next step" title="Recommended lessons"/>
            <div className="mt-4 space-y-2">
              {recommended_lessons.map((lsn) => (
                <Link
                  key={lsn.id}
                  to={`/app/learn/lesson/${lsn.id}`}
                  data-testid={`hub-lesson-${lsn.id}`}
                  className="flex items-start justify-between gap-3 p-3 lg:p-4 rounded-2xl bg-beige hover:bg-[#EAE5D9] transition-smooth"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-navy text-sm lg:text-base leading-snug truncate">{lsn.title}</p>
                    {lsn.summary && <p className="text-xs lg:text-sm text-navy/60 mt-0.5 leading-relaxed line-clamp-2">{lsn.summary}</p>}
                    <p className="text-[10px] uppercase tracking-widest text-navy/40 mt-1">{lsn.duration}</p>
                  </div>
                  <ArrowRight size={16} className="text-navy/40 mt-1 flex-shrink-0"/>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Coach conversation */}
        {coach && (
          <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="hub-coach">
            <SectionHeader icon={MessageCircle} eyebrow="Talk it through" title="Coach conversation"/>
            <p className="text-sm lg:text-base text-navy/70 mt-3 leading-relaxed">
              Bring this reflection into a private conversation with your AI Coach. He knows the
              pattern you just surfaced and can help you think through what to do with it.
            </p>
            <Link
              to={coach.cta_to}
              state={{ starter: coach.starter, topic: coach.topic }}
              data-testid="hub-coach-cta"
              className="mt-4 inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-navy-soft transition-smooth"
            >
              Start with the Coach <ArrowRight size={14}/>
            </Link>
          </div>
        )}

        {/* Growth plan */}
        {growth_plan && (
          <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="hub-plan">
            <SectionHeader icon={Milestone} eyebrow="Practice daily" title="Recommended growth plan"/>
            <div className="mt-4 flex items-start justify-between gap-4">
              <div>
                <p className="font-heading text-lg lg:text-xl text-navy">{growth_plan.title}</p>
                {growth_plan.description && (
                  <p className="text-sm text-navy/60 mt-1 leading-relaxed">{growth_plan.description}</p>
                )}
                <p className="text-[10px] uppercase tracking-widest text-navy/40 mt-2">
                  {growth_plan.days}-day plan
                </p>
              </div>
              <Link
                to={growth_plan.cta_to}
                data-testid="hub-plan-cta"
                className="flex-shrink-0 inline-flex items-center gap-1 bg-gold text-navy px-4 py-2 rounded-full text-sm font-medium hover:bg-gold-soft transition-smooth"
              >
                View plan <ArrowRight size={14}/>
              </Link>
            </div>
          </div>
        )}

        {/* Journal reflection */}
        {journal && (
          <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="hub-journal">
            <SectionHeader icon={Feather} eyebrow="Write it out" title="Journal reflection"/>
            <div className="mt-4 bg-beige rounded-2xl p-4 lg:p-5">
              <p className="text-sm lg:text-base text-navy/85 italic leading-relaxed">
                &ldquo;{journal.prompt}&rdquo;
              </p>
            </div>
            <Link
              to={journal.cta_to}
              state={{ prompt: journal.prompt }}
              data-testid="hub-journal-cta"
              className="mt-4 inline-flex items-center gap-2 text-navy text-sm font-medium hover:underline"
            >
              Open journal with this prompt <ArrowRight size={14}/>
            </Link>
          </div>
        )}

        <p className="text-xs text-navy/50 text-center leading-relaxed pt-2">
          Educational, not diagnostic. If you&apos;re working through something heavy, consider
          reaching out to a culturally responsive therapist.
        </p>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, eyebrow, title }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold inline-flex items-center gap-2">
        <Icon size={12}/> {eyebrow}
      </p>
      <p className="font-heading text-xl lg:text-2xl text-navy mt-1.5">{title}</p>
    </div>
  );
}

function ScoreCard({ label, value, max, testid }) {
  const pct = Math.max(0, Math.min(100, ((value + max) / (max * 2)) * 100));
  return (
    <div className="bg-white/10 rounded-2xl p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-widest text-white/60">{label}</p>
      <p className="font-heading text-2xl lg:text-3xl mt-1">
        {value}<span className="text-sm text-white/40"> / {max}</span>
      </p>
      <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
        <div className="h-full bg-gold" style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}

function MarkdownLite({ text }) {
  return (
    <div className="text-navy/85 leading-relaxed space-y-4" data-testid="hub-ai-advice-text">
      {text.split(/\n{2,}/).map((block, bi) => {
        const boldMatch = block.match(/^\*\*(.+?)\*\*\s*$/);
        if (boldMatch) {
          return <p key={bi} className="font-heading text-lg lg:text-xl text-navy mt-1">{boldMatch[1]}</p>;
        }
        const parts = block.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={bi} className="text-sm lg:text-base leading-relaxed whitespace-pre-wrap">
            {parts.map((p, pi) => {
              const m = p.match(/^\*\*(.+)\*\*$/);
              return m ? <strong key={pi} className="font-semibold text-navy">{m[1]}</strong> : <span key={pi}>{p}</span>;
            })}
          </p>
        );
      })}
    </div>
  );
}
